
















import {Vue, Component, Mixins, Prop} from 'vue-property-decorator';
import UserRoleMixin from '@/mixins/UserRoleMixin.vue';
import bookingDefault from '@/instance-defaults/bookingDefault';
import {BOOKING_STATUS, CALENDAR_FORMAT, CLAIM_STATUS, getStoreSettings, multiNil, nil} from '@/common';
import GlobalAlertMixin from '@/mixins/GlobalAlertMixin.vue';
import {DateTime} from 'luxon';
import ChangeBookingDialog from '@/components/Views/Dashboard/Booking/ChangeBookingDialog.vue';
import ChangeBookingCheckInOut from '@/components/Views/Dashboard/Booking/ChangeBookingCheckInOut.vue';
import BookingCalculateCheckInOutMixin from '@/mixins/BookingCalculateCheckInOutMixin.vue';
import {getZonedBookingDate} from '@/utils';

@Component({
    components: {ChangeBookingCheckInOut, ChangeBookingDialog},
})
export default class BookingActions extends Mixins(UserRoleMixin, GlobalAlertMixin, BookingCalculateCheckInOutMixin) {
    @Prop({default: bookingDefault})
    public booking!: any;

    @Prop({default: () => null})
    public claim!: any;

    @Prop({default: false})
    public loading!: boolean;

    @Prop({default: 0})
    public maxWidth!: number;

    get cannotAccept() {
        return this.isCancelled || this.isAssigned || this.isAccepted || !this.hasClaim;
    }
    get cannotReject() {
        return this.isCancelled || this.isRejected || !this.hasClaim;
    }
    get withinCheckInWindow() {
        if (nil(this.expectedCheckInTime)) { return false; }
        return this.timeIsWithinMinutes(this.expectedZonedCheckInTime, this.checkInBuffer) === 0;
    }
    get withinCheckOutWindow() {
        if (nil(this.expectedCheckOutTime)) { return false; }
        return this.timeIsWithinMinutes(this.expectedZonedCheckOutTime, this.checkInBuffer) === 0;
    }

    get canCheckIn() {
        return !this.isCancelled && !this.checkedIn && this.withinCheckInWindow;
    }
    get canCheckOut() {
        return !this.isCancelled && !this.checkedOut && this.withinCheckOutWindow;
    }

    get acceptText() {
        return this.isAssigned ? 'Assigned' : this.isAccepted ? 'Accepted' : 'Accept';
    }
    get rejectText() {
        return this.isRejected ? 'Rejected' : 'Reject';
    }

    get checkInText() {
        if (!this.checkedIn) {
            const range = this.timeIsWithinMinutes(this.expectedZonedCheckInTime, this.checkInBuffer);
            switch (range) {
                case -1: return 'Too Late to Check In';
                case 1: return 'Too Early to Check In';
                default: return 'Check In';
            }
        }
        return `Checked In ${this.friendlyCheckInOutTime(this.booking.checkInTime)}`;
    }
    get checkOutText() {
        if (!this.checkedOut) {
            const range = this.timeIsWithinMinutes(this.expectedZonedCheckOutTime, this.checkInBuffer);
            switch (range) {
                case -1: return 'Too Late to Check Out';
                case 1: return 'Too Early to Check Out';
                default: return 'Check Out';
            }
        }
        return `Checked Out ${this.friendlyCheckInOutTime(this.booking.checkOutTime)}`;
    }

    get checkedIn() {
        if (nil(this.booking)) {
            return false;
        }
        return !nil(this.booking.checkInTime);
    }

    get checkedOut() {
        if (nil(this.booking)) {
            return false;
        }
        return !nil(this.booking.checkOutTime);
    }

    get hasClaim() {
        return !nil(this.claim);
    }

    get isRejected() {
        return this.hasClaim && this.claim.status === CLAIM_STATUS.REJECTED;
    }

    get isAccepted() {
        return this.hasClaim && this.claim.status === CLAIM_STATUS.ACCEPTED;
    }

    get isAssigned() {
        return this.hasClaim && this.claim.status === CLAIM_STATUS.ASSIGNED;
    }

    get isCancelled(){
        return this.booking.status === BOOKING_STATUS.CANCELLED;
    }

    get bookingIsMine() {
        if (multiNil(this, '$store.state.auth.user.vendorModel')) { return false; }
        const vendorId = this.$store.state.auth.user.vendorModel._id;
        return this.hasClaim && (this.isAccepted || this.isAssigned) && vendorId === this.claim.vendor;
    }

    public async respondToBooking(accept: boolean) {
        if (!nil(this.claim)) {
            const status = accept ? CLAIM_STATUS.ACCEPTED : CLAIM_STATUS.REJECTED;
            const oldStatus = this.claim.status.toString();
            const {vendorModel} = this.$store.state.auth.user;
            const knownStatus = this.claim.status;
            try {
                this.claim.status = status;
                await this.claim.save();
                this.$emit('onClaimChanged', {claim: this.claim, oldValue: oldStatus, value: status});
            } catch (e) {

                this.claim.status = knownStatus;
                let addToAlert = {};
                if (e.code === 409 && !multiNil(e, 'data.clash')) {
                    addToAlert = {
                        button: {
                            text: 'View Conflict',
                            link: `bookings/${e.data.clash}`,
                        },
                        timeout: 7000,
                    };
                }
                this.$bus.$emit('showAlert', {
                    type: 'error',
                    text: e.message,
                    ...addToAlert,
                });
            }
        }
    }

    public async check(checkingIn: boolean = true) {
        if (checkingIn) {
            this.booking.checkInTime = DateTime.utc().toISO();
        } else {
            this.booking.checkOutTime = DateTime.utc().toISO();
        }
        const text = checkingIn ? 'in' : 'out';
        try {
            await this.booking.save();
            this.$emit('onBookingChanged', {booking: this.booking});
            this.showAlert(`You've checked ${text}!`, 'success');
        } catch (e) {
            console.log(e);
            this.showAlert(`There was an error checking ${text}`, 'error');
        }
    }
    public async getBuffer() {
        const settings = await getStoreSettings(this.$FeathersVuex.Setting);
        if (!nil(settings.checkInBuffer)) {
            this.checkInBuffer = settings.checkInBuffer;
        }
    }


    public mounted() {
        this.getBuffer();
    }

}
